import React from 'react';
import { ApplicationFormSubStatus } from '@brighte/validation';
import { ModifiedApplicationQuote } from '@graphql/types';
import { FINANCE_PURCHASE, productIds } from '@utils';
import { getAnnualTermRates } from './helpers';

function getDisclaimers({
  quote,
  applicationSubStatus,
  selectedTerm,
  isCashApplication,
}: {
  quote?: ModifiedApplicationQuote;
  applicationSubStatus?: string | null;
  selectedTerm?: string | number;
  isCashApplication?: boolean | undefined;
}) {
  const {
    interestRate,
    latePaymentFeeForFlex,
    financedAmount,
    establishmentFee,
    totalMinimumAmountPayable,
    fortnightlyRepaymentAmount,
    feeChargeForBNPL,
  } = FINANCE_PURCHASE.DISCLAIMERS;
  const { financialProduct } = quote ?? {};

  if (!financialProduct) return [];

  const [min, max] = selectedTerm ? getAnnualTermRates(selectedTerm, quote) : [0, 0];
  const { riskBasedPricing, minEstablishmentFee, maxEstablishmentFee } = quote?.productConfiguration ?? {
    minEstablishmentFee: 0,
    maxEstablishmentFee: 0,
    riskBasedPricing: false,
  };

  const generateDisclaimerTerms = (): string[] | React.ReactNode[] => {
    const disclaimerTermsOnContractAcceptancePage = applicationSubStatus === ApplicationFormSubStatus.PendingAcceptance;

    switch (financialProduct.id) {
      case productIds.FLEX:
        return [latePaymentFeeForFlex()];
      case productIds.ELECTRIFY_2515: {
        if (disclaimerTermsOnContractAcceptancePage) {
          return [];
        }
        if (!isCashApplication) {
          return [
            interestRate(min, max),
            financedAmount(),
            totalMinimumAmountPayable(financialProduct.id),
            establishmentFee(minEstablishmentFee, maxEstablishmentFee),
          ];
        }
        return [];
      }
      case productIds.BGL:
      case productIds.BPL:
      case productIds.HEUF_LOAN: {
        // If the application is pending acceptance, only show the total minimum amount payablebgl
        if (disclaimerTermsOnContractAcceptancePage) {
          return [];
        }

        if (riskBasedPricing) {
          return [interestRate(min, max), totalMinimumAmountPayable()];
        }

        return [totalMinimumAmountPayable()];
      }
      case productIds.ACT_SHS:
        return [totalMinimumAmountPayable()];
      case productIds.BRIGHTE_PAY: {
        return [fortnightlyRepaymentAmount, feeChargeForBNPL];
      }
      default:
        return [];
    }
  };

  const disclaimerTerms = generateDisclaimerTerms();

  return disclaimerTerms?.map((term, index) => ({
    symbol: `${index + 1}`,
    message: term,
  }));
}

export default getDisclaimers;
